import React, { memo } from 'react';
import { useTranslation } from 'react-i18next';
import {
  Dialog,
  Box,
  Typography,
  Button,
  CircularProgress,
} from '@mui/material';
import { styled } from '@mui/material/styles';
import ErrorOutlineIcon from '@mui/icons-material/ErrorOutline';
import { T_KEYS } from '../../../constants';
import { IMsoolUnit, ChannelType } from '../../../types';

// Import unified channel logo components
import { ChannelLogo, ChannelLogoType } from '../../ChannelLogos';

// Figma Design: Modal width 526px, shadow, 24px border-radius
const StyledDialog = styled(Dialog)({
  '& .MuiBackdrop-root': {
    backgroundColor: 'rgba(255, 255, 255, 0.48)',
    backdropFilter: 'blur(2.6px)',
  },
  '& .MuiDialog-paper': {
    borderRadius: '24px',
    maxWidth: '526px',
    width: '100%',
    padding: '0',
    margin: '16px',
    boxShadow: '2px 4px 20px 0px rgba(0, 0, 0, 0.1)',
    overflow: 'hidden',
  },
});

// Figma: Content container with 32px padding, 24px gap
const ContentContainer = styled(Box)({
  display: 'flex',
  flexDirection: 'column',
  alignItems: 'center',
  justifyContent: 'center',
  padding: '32px',
  gap: '24px',
  backgroundColor: '#ffffff',
  borderTopLeftRadius: '24px',
  borderTopRightRadius: '24px',
});

// Figma: Icon container 56px, bg rgba(225,25,0,0.1), border-radius 28px
const IconContainer = styled(Box)({
  width: '56px',
  height: '56px',
  borderRadius: '28px',
  backgroundColor: 'rgba(225, 25, 0, 0.1)',
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'center',
});

// Figma: Message container with 16px gap
const MessageContainer = styled(Box)({
  display: 'flex',
  flexDirection: 'column',
  alignItems: 'center',
  justifyContent: 'center',
  gap: '16px',
  width: '100%',
});

// Figma: Title - IBM Plex Sans Arabic Bold, 20px, line-height 28px, #262626
const Title = styled(Typography)({
  fontFamily: '"IBM Plex Sans Arabic", "Tajawal", sans-serif',
  fontSize: '20px',
  fontWeight: 700,
  lineHeight: '28px',
  color: '#262626',
  textAlign: 'center',
  width: '100%',
});

// Figma: Description - IBM Plex Sans Arabic Regular, 16px, line-height 24px, #666666
const Description = styled(Typography)({
  fontFamily: '"IBM Plex Sans Arabic", "Tajawal", sans-serif',
  fontSize: '16px',
  fontWeight: 400,
  lineHeight: '24px',
  color: '#666666',
  textAlign: 'center',
  width: '100%',
});

// Figma: Unit preview card - bg #F9FAFB, border 1px #E5E7EB, border-radius 20px, padding 16px
const UnitPreviewCard = styled(Box)({
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'space-between',
  padding: '16px',
  backgroundColor: '#F9FAFB',
  border: '1px solid #E5E7EB',
  borderRadius: '20px',
  width: '100%',
  maxWidth: '462px',
});

const ChannelLogosContainer = styled(Box)({
  display: 'flex',
  alignItems: 'center',
  gap: '8px',
});

// Note: Local ChannelLogo styled component removed - using unified ChannelLogo component instead

const UnitInfoContainer = styled(Box)({
  display: 'flex',
  alignItems: 'center',
  gap: '12px',
});

// Figma: Unit name - IBM Plex Sans Arabic Bold, 16px, line-height 24px, #331860
const UnitName = styled(Typography)({
  fontFamily: '"IBM Plex Sans Arabic", "Tajawal", sans-serif',
  fontSize: '16px',
  fontWeight: 700,
  lineHeight: '24px',
  color: '#331860',
  textAlign: 'end',
});

// Figma: Unit image - 84px x 40px, border-radius 2.536px
const UnitImage = styled('img')({
  width: '84px',
  height: '40px',
  borderRadius: '2.536px',
  objectFit: 'cover',
  backgroundColor: '#f0f0f0',
});

// Figma: Footer container with 24px padding, border-top 1px #F2F2F2, border-radius bottom 24px
const FooterContainer = styled(Box)({
  display: 'flex',
  flexDirection: 'column',
  alignItems: 'flex-start',
  padding: '24px',
  backgroundColor: '#ffffff',
  borderTop: '1px solid #F2F2F2',
  borderBottomLeftRadius: '24px',
  borderBottomRightRadius: '24px',
});

// Figma: Buttons container with 12px gap, height 44px
const ButtonsContainer = styled(Box)({
  display: 'flex',
  gap: '12px',
  width: '100%',
  height: '44px',
});

// Figma: Cancel button - border 1px #E4E4E7, border-radius 8px, font SemiBold 16px, color #1A1A1A
const CancelButton = styled(Button)({
  flex: 1,
  height: '44px',
  borderRadius: '8px',
  border: '1px solid #E4E4E7',
  backgroundColor: '#ffffff',
  fontFamily: '"IBM Plex Sans Arabic", "Tajawal", sans-serif',
  fontSize: '16px',
  fontWeight: 600,
  lineHeight: '24px',
  color: '#1A1A1A',
  textTransform: 'none',
  '&:hover': {
    backgroundColor: '#F9FAFB',
    border: '1px solid #E4E4E7',
  },
  '&:disabled': {
    color: '#BFBFBF',
  },
});

// Figma: Confirm button - bg #5B2FF9, border-radius 8px, font SemiBold 16px, color white
const ConfirmButton = styled(Button)({
  flex: 1,
  height: '44px',
  borderRadius: '8px',
  backgroundColor: '#5B2FF9',
  fontFamily: '"IBM Plex Sans Arabic", "Tajawal", sans-serif',
  fontSize: '16px',
  fontWeight: 600,
  lineHeight: '24px',
  color: '#ffffff',
  textTransform: 'none',
  '&:hover': {
    backgroundColor: '#4a25d4',
  },
  '&:disabled': {
    backgroundColor: '#BFBFBF',
    color: '#ffffff',
  },
});

// Helper to get channel logo type
const getChannelLogoType = (channelType: ChannelType): ChannelLogoType | null => {
  switch (channelType) {
    case ChannelType.Airbnb:
      return 'airbnb';
    case ChannelType.Booking:
      return 'booking';
    case ChannelType.Gathern:
      return 'gathern';
    default:
      return null;
  }
};

interface UnmapConfirmModalProps {
  open: boolean;
  onClose: () => void;
  onConfirm: () => void;
  isLoading?: boolean;
  unit?: IMsoolUnit | null;
}

const UnmapConfirmModal: React.FC<UnmapConfirmModalProps> = memo(({
  open,
  onClose,
  onConfirm,
  isLoading,
  unit,
}) => {
  const { t } = useTranslation();

  // Get unique channel types from the unit's connected channels (status === true), excluding Airbnb
  const connectedChannels = unit?.channels?.filter(c => c.status === true && c.channelType !== ChannelType.Airbnb) || [];
  const channelTypes = connectedChannels.map(c => c.channelType);
  const uniqueChannelTypes = [...new Set(channelTypes)];

  return (
    <StyledDialog open={open} onClose={onClose}>
      <ContentContainer>
        {/* Warning Icon */}
        <IconContainer>
          <ErrorOutlineIcon sx={{ fontSize: 28, color: '#E11900' }} />
        </IconContainer>

        {/* Message */}
        <MessageContainer>
          <Title>
            {t(T_KEYS.UNITS_MAPPING_UNMAP_CONFIRM_TITLE, 'هل تريد بالتأكيد فكّ دمج هذه الوحدة؟')}
          </Title>
          <Description>
            {t(T_KEYS.UNITS_MAPPING_UNMAP_CONFIRM_MESSAGE, 'سيتم فصل هذه الوحدة عن المجموعة المدمجة وإعادتها إلى قائمتها الأصلية في Airbnb أو Booking أو Gathering.')}
          </Description>

          {/* Unit Preview Card */}
          {unit && (
            <UnitPreviewCard>
              <UnitInfoContainer>
                {unit.imageUrl && (
                  <UnitImage
                  src={unit.imageUrl}
                  alt={unit.propertyName || 'Unit'}
                  onError={(e) => {
                      (e.target as HTMLImageElement).style.display = 'none';
                    }}
                    />
                )}
                <UnitName>
                  {unit.propertyName || t('CHANNELS.UNITS_MAPPING.UNNAMED_UNIT', 'وحدة بدون اسم')}
                </UnitName>
              </UnitInfoContainer>
              <ChannelLogosContainer>
                {uniqueChannelTypes.map((channelType, index) => {
                  const logoType = getChannelLogoType(channelType);
                  return logoType ? (
                    <ChannelLogo
                      key={index}
                      channel={logoType}
                      size="sm"
                      rounded
                    />
                  ) : null;
                })}
              </ChannelLogosContainer>
            </UnitPreviewCard>
          )}
        </MessageContainer>
      </ContentContainer>

      {/* Footer with Buttons */}
      <FooterContainer>
        <ButtonsContainer>
          <ConfirmButton onClick={onConfirm} disabled={isLoading} variant="contained">
            {isLoading ? (
              <CircularProgress size={20} sx={{ color: '#ffffff' }} />
            ) : (
              t(T_KEYS.UNITS_MAPPING_UNMAP_CONFIRM_YES, 'فك الدمج')
            )}
          </ConfirmButton>
          <CancelButton onClick={onClose} disabled={isLoading} variant="outlined">
            {t(T_KEYS.UNITS_MAPPING_UNMAP_CONFIRM_NO, 'إلغاء')}
          </CancelButton>
        </ButtonsContainer>
      </FooterContainer>
    </StyledDialog>
  );
});

UnmapConfirmModal.displayName = 'UnmapConfirmModal';

export default UnmapConfirmModal;
